import {
    isLimiteApiExternaAxiosError,
    getLimiteApiExternaPayload,
} from '@/utils/limiteApiExterna';

export const ErrorCategories = {
    API: 'API',
    AUTENTICACAO: 'AUTENTICACAO',
    VALIDACAO: 'VALIDACAO',
    REDE: 'REDE',
    SISTEMA: 'SISTEMA'
};

// Função para obter a tradução da categoria
export function getErrorCategoryTranslation(category, $t) {
    if (!$t || typeof $t !== 'function') {
        // Retorna valores padrão em português se não houver tradução disponível
        const defaults = {
            'API': 'API',
            'AUTENTICACAO': 'Autenticação',
            'VALIDACAO': 'Validação',
            'REDE': 'Rede',
            'SISTEMA': 'Sistema'
        };
        return defaults[category] || category;
    }
    
    try {
        const translations = {
            'API': $t('system.errors.categories.API'),
            'AUTENTICACAO': $t('system.errors.categories.AUTENTICACAO'),
            'VALIDACAO': $t('system.errors.categories.VALIDACAO'),
            'REDE': $t('system.errors.categories.REDE'),
            'SISTEMA': $t('system.errors.categories.SISTEMA')
        };
        
        return translations[category] || category;
    } catch (error) {
        console.warn('Error translating category:', error);
        const defaults = {
            'API': 'API',
            'AUTENTICACAO': 'Autenticação',
            'VALIDACAO': 'Validação',
            'REDE': 'Rede',
            'SISTEMA': 'Sistema'
        };
        return defaults[category] || category;
    }
}

export const ErrorCategoryIcons = {
    [ErrorCategories.API]: 'mdi-api',
    [ErrorCategories.AUTENTICACAO]: 'mdi-lock-alert',
    [ErrorCategories.VALIDACAO]: 'mdi-alert-circle-check',
    [ErrorCategories.REDE]: 'mdi-wifi-off',
    [ErrorCategories.SISTEMA]: 'mdi-alert-circle'
};

export const ErrorCategoryColors = {
    [ErrorCategories.API]: '#ff6b6b',
    [ErrorCategories.AUTENTICACAO]: '#ff9800',
    [ErrorCategories.VALIDACAO]: '#ffc107',
    [ErrorCategories.REDE]: '#2196f3',
    [ErrorCategories.SISTEMA]: '#d60000'
};

function isNetworkError(error) {
    if (!error) return false;
    
    // Erros de conexão
    if (error.message) {
        const msg = error.message.toLowerCase();
        if (
            msg.includes('network error') ||
            msg.includes('networkerror') ||
            msg.includes('timeout') ||
            msg.includes('connection refused') ||
            msg.includes('connection failed') ||
            msg.includes('connection reset') ||
            msg.includes('failed to fetch') ||
            msg.includes('err_network') ||
            msg.includes('econnrefused') ||
            msg.includes('etimedout')
        ) {
            return true;
        }
    }
    
    // Axios sem resposta (geralmente indica problema de rede)
    if (error.code) {
        const code = error.code.toLowerCase();
        if (
            code === 'econnrefused' ||
            code === 'etimedout' ||
            code === 'enetunreach' ||
            code === 'ecanceled'
        ) {
            return true;
        }
    }
    
    // Sem response do axios geralmente indica problema de rede
    if (error.request && !error.response) {
        return true;
    }
    
    return false;
}

function isAuthenticationError(error) {
    if (!error || !error.response) return false;
    if (isLimiteApiExternaAxiosError(error)) return false;

    const status = error.response.status;
    return status === 401 || status === 403;
}

function isValidationError(error) {
    if (!error) return false;
    
    // Erro 400 com mensagem de validação
    if (error.response && error.response.status === 400) {
        const data = error.response.data;
        if (
            data &&
            (
                data.mensagem?.toLowerCase().includes('validação') ||
                data.mensagem?.toLowerCase().includes('validacao') ||
                data.mensagem?.toLowerCase().includes('inválido') ||
                data.mensagem?.toLowerCase().includes('invalido') ||
                data.mensagem?.toLowerCase().includes('campo obrigatório') ||
                data.mensagem?.toLowerCase().includes('required') ||
                data.errors || // Laravel validation errors
                data.validation || // Custom validation errors
                Array.isArray(data) // Validation error array
            )
        ) {
            return true;
        }
    }
    
    return false;
}

function isAPIError(error) {
    if (!error) return false;
    
    // Se tem response, é um erro de API
    if (error.response && error.response.status) {
        // Mas não é autenticação nem validação
        if (!isAuthenticationError(error) && !isValidationError(error)) {
            return true;
        }
    }
    
    return false;
}

export function categorizeError(error, defaultCategory = ErrorCategories.SISTEMA) {
    // Se já foi especificada uma categoria explicitamente
    if (error && typeof error === 'object' && error.category) {
        return error.category;
    }
    
    // Se o erro é uma string simples, verifica se há indicação de categoria
    if (typeof error === 'string') {
        // Pode ser que seja uma mensagem que indica a origem
        return defaultCategory;
    }
    
    if (!error || typeof error !== 'object') {
        return defaultCategory;
    }
    
    // Prioridade: Rede > Limite API externa > Autenticação > Validação > API > Sistema
    if (isNetworkError(error)) {
        return ErrorCategories.REDE;
    }

    if (isLimiteApiExternaAxiosError(error)) {
        return ErrorCategories.API;
    }
    
    if (isAuthenticationError(error)) {
        return ErrorCategories.AUTENTICACAO;
    }
    
    if (isValidationError(error)) {
        return ErrorCategories.VALIDACAO;
    }
    
    if (isAPIError(error)) {
        return ErrorCategories.API;
    }
    
    return defaultCategory;
}

export function extractErrorMessage(error, $t = null) {
    const Translate = (key, fallback) => {
        if (!$t || typeof $t !== 'function') return fallback;
        try {
            return $t(key);
        } catch (e) {
            console.warn('Translation error:', e);
            return fallback;
        }
    };

    if (!error) {
        return Translate('system.errors.messages.unknownError', 'Erro desconhecido');
    }

    if (typeof error === 'string') {
        return error;
    }

    // Sem resposta: CORS, proxy, servidor indisponível, ou multipart sem boundary no Content-Type
    if (isNetworkError(error) && !error.response) {
        return Translate(
            'system.errors.messages.networkOrConnectionError',
            'Não foi possível falar com o servidor. Verifique a rede, a URL da API e o proxy. Em envio de arquivos, deixe o navegador definir o Content-Type do multipart (não use multipart/form-data sem boundary).'
        );
    }

    // Com resposta HTTP, priorizar o corpo da API (mensagem / message), não o texto genérico do Axios
    // ("Request failed with status code 409", etc.)
    if (error.response) {
        const data = error.response.data;
        const status = error.response.status;

        if (data != null) {
            if (typeof data === 'string' && String(data).trim()) {
                return String(data).trim();
            }
            if (typeof data === 'object') {
                if (data.mensagem) return data.mensagem;
                if (data.message) return data.message;
                if (data.msg) return data.msg;
                if (typeof data.result === 'string' && data.result.trim()) return data.result;
                if (data.error) {
                    if (typeof data.error === 'string') return data.error;
                    if (data.error.message) return data.error.message;
                }
                if (Array.isArray(data)) {
                    return data
                        .map((err) =>
                            typeof err === 'string' ? err : err.message || err.msg
                        )
                        .join(', ');
                }
                if (data.errors && typeof data.errors === 'object') {
                    const messages = [];
                    for (const field in data.errors) {
                        if (Array.isArray(data.errors[field])) {
                            messages.push(`${field}: ${data.errors[field].join(', ')}`);
                        } else {
                            messages.push(`${field}: ${data.errors[field]}`);
                        }
                    }
                    if (messages.length > 0) return messages.join('; ');
                }
            }
        }

        const bodyMsg =
            data && typeof data === 'object'
                ? data.mensagem || data.message || data.msg
                : null;

        if (status === 401) {
            return (
                bodyMsg ||
                Translate(
                    'system.errors.messages.unauthorizedAccess',
                    'Usuário não tem permissão para acessar este recurso'
                )
            );
        }
        if (status === 403) {
            const limitePayload = getLimiteApiExternaPayload(error.response);
            if (limitePayload) {
                return (
                    limitePayload.mensagem ||
                    limitePayload.message ||
                    Translate(
                        'system.errors.messages.apiLimitExceeded',
                        'Limite de chamadas da API externa excedido para este serviço. Aguarde a renovação do período ou solicite ao administrador um limite maior.'
                    )
                );
            }
            return (
                bodyMsg ||
                Translate('system.errors.messages.accessDenied', 'Acesso negado')
            );
        }
        if (status === 404) {
            return (
                bodyMsg ||
                Translate(
                    'system.errors.messages.resourceNotFound',
                    'Recurso não encontrado'
                )
            );
        }
        if (status === 409) {
            return (
                bodyMsg ||
                Translate(
                    'system.errors.messages.conflict',
                    'Conflito: registro duplicado ou estado incompatível com a operação.'
                )
            );
        }
        if (status === 500) {
            return (
                bodyMsg ||
                Translate(
                    'system.errors.messages.internalServerError',
                    'Erro interno do servidor'
                )
            );
        }
        if (status === 400) {
            return (
                bodyMsg ||
                Translate(
                    'system.errors.messages.invalidRequest',
                    'Requisição inválida'
                )
            );
        }

        if (bodyMsg) return bodyMsg;
    }

    if (error.message && typeof error.message === 'string') {
        return error.message;
    }

    return Translate(
        'system.errors.messages.unknownSystemError',
        'Erro desconhecido de sistema'
    );
}

export function prepareErrorForDisplay(error, explicitCategory = null, $t = null) {
    const category = explicitCategory || categorizeError(error);
    const message = extractErrorMessage(error, $t);
    const status = error?.response?.status || null;
    
    return {
        category,
        message,
        status
    };
}
